import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import Icon from "@/components/Icon";
import { ProcessTimeline } from "@/components/home/ProcessSection";
import DataJourney from "@/components/home/DataJourney";
import FinalCTA from "@/components/home/FinalCTA";
import { PrimaryCTA } from "@/components/CTAButton";
import { SOLUTIONS, SOLUTION_DETAIL_SLUGS, IMAGES } from "@/data/content";

export default function SolutionDetail() {
  const { slug } = useParams();
  if (!SOLUTION_DETAIL_SLUGS.includes(slug)) return <Navigate to="/solutions" replace />;
  const s = SOLUTIONS.find((x) => x.slug === slug);

  return (
    <>
      <SEO title={`${s.title} | All Data Tech`} description={s.intro} path={`/solutions/${slug}`} />

      {/* Editorial split hero */}
      <section className="relative bg-[#F7F8FA] pt-28 lg:pt-36" data-testid="solution-hero">
        <div className="ad-container grid items-center gap-10 pb-16 lg:grid-cols-2 lg:gap-16 lg:pb-24">
          <div>
            <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#1677FF]">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1677FF]/10"><Icon name={s.icon} className="h-4 w-4" /></span>
              {s.title}
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="mt-5 display-lg text-[#111827]">
              {s.hero}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="mt-6 max-w-xl text-base leading-relaxed text-[#4B5563] sm:text-lg">
              {s.intro}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} className="mt-8">
              <PrimaryCTA to="/consultation" testId="solution-detail-cta">Book a Consultation</PrimaryCTA>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.8 }} className="relative overflow-hidden rounded-[1.5rem] border border-[#111827]/8 shadow-[0_40px_80px_-45px_rgba(17,24,39,0.4)]">
            <div className="img-overlay-blue relative">
              <img src={IMAGES[s.image]} alt={`${s.title} visual`} className="h-72 w-full object-cover lg:h-[460px]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities + use cases */}
      <section className="ad-section bg-white">
        <div className="ad-container grid gap-12 lg:grid-cols-2">
          <Reveal>
            <div>
              <span className="ad-eyebrow">What it covers</span>
              <h2 className="mt-4 text-2xl font-extrabold text-[#111827] sm:text-3xl">Capabilities</h2>
              <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
                {s.covers.map((c) => (
                  <div key={c} className="flex items-center gap-2 rounded-xl border border-[#111827]/8 bg-[#F7F8FA] px-4 py-3 text-sm text-[#374151]">
                    <Check className="h-4 w-4 flex-shrink-0 text-[#1677FF]" /> {c}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal index={1}>
            <div>
              <span className="ad-eyebrow">Where it helps</span>
              <h2 className="mt-4 text-2xl font-extrabold text-[#111827] sm:text-3xl">Typical use cases</h2>
              <ul className="mt-6 space-y-3">
                {s.useCases.map((u, i) => (
                  <li key={u} className="flex items-start gap-3 text-sm text-[#4B5563]">
                    <span className="mt-0.5 text-xs font-bold text-[#1677FF]">{String(i + 1).padStart(2, "0")}</span> {u}
                  </li>
                ))}
              </ul>
              <div className="mt-8 rounded-2xl border border-[#111827]/8 bg-[#F7F8FA] p-6">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1677FF]">Technology</span>
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.tech.map((t) => (
                    <span key={t} className="rounded-lg border border-[#111827]/10 bg-white px-3 py-1.5 text-sm text-[#374151]">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <DataJourney />

      <section className="ad-section bg-white">
        <div className="ad-container">
          <div className="max-w-2xl">
            <span className="ad-eyebrow">Engagement</span>
            <h2 className="mt-4 text-2xl font-extrabold text-[#111827] sm:text-3xl">How we deliver</h2>
          </div>
          <div className="mt-14"><ProcessTimeline /></div>

          <div className="mt-16 flex flex-wrap gap-3">
            {SOLUTIONS.filter((x) => x.slug !== slug && SOLUTION_DETAIL_SLUGS.includes(x.slug)).map((r) => (
              <Link key={r.slug} to={`/solutions/${r.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#111827]/12 bg-white px-4 py-2 text-sm text-[#374151] transition-colors hover:border-[#1677FF] hover:text-[#1677FF]">
                {r.title} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
